import React, { useCallback, useEffect, useState } from 'react'
import _debounce from 'lodash.debounce'

import './styles.scss'
import { intranetApi } from 'api'
import { Button } from 'components/Form'
import InputField from '../InputField'
import LabelItem from './LabelItem'
import Modal from './Modal'
import { lang } from 'utils/helpers'

const LabelsSection = () => {
  const [text, setText] = useState('')
  const [page, setPage] = useState(0)
  const [labels, setLabels] = useState([])
  const [labelIdForEdit, setLabelIdForEdit] = useState('')
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isAllLabels, setIsAllLabels] = useState(false)
  const [loadingModal, setLoadingModal] = useState(false)
  const [errorModal, setErrorModal] = useState('')
  const pageSize = 30

  useEffect(() => {
    getLabels()
  }, [page])

  const getLabels = (params = {}) => {
    const { text } = params

    const data = { page, pageSize }
    if (text) data.text = text

    setLoading(true)
    intranetApi.translations.getLabelsList(data)
      .then(({ data }) => {
        if (!page) {
          setLabels(data)
        } else {
          setLabels(prevState => [...prevState, ...data])
        }
        if (data.length < pageSize) setIsAllLabels(true)
      })
      .finally(() => setLoading(false))
  }

  const toggleModal = id => {
    if (!isOpenModal) {
      if (id) setLabelIdForEdit(id)
    } else {
      setLabelIdForEdit('')
    }
    setIsOpenModal(prevState => !prevState)
  }

  const handleSearch = useCallback(_debounce(text => getLabels({ text }), 250), [])

  const handleOnChange = text => {
    setText(text)
    handleSearch(text)
  }

  const handleSave = ({ name, color }) => {
    setLoadingModal(true)
    if (labelIdForEdit) {
      intranetApi.translations.updateLabel({ id: labelIdForEdit, text: name, color })
        .then(() => {
          const updatedLabels = [...labels]
          const labelIndex = updatedLabels.findIndex(({ _id }) => _id === labelIdForEdit)
          updatedLabels[labelIndex] = { ...updatedLabels[labelIndex], name, color }
          setLabels(updatedLabels)
          toggleModal()
        })
        .catch(() => setErrorModal(lang('dashboard_error-saving-label')))
        .finally(() => setLoadingModal(false))
    } else {
      intranetApi.translations.createLabel({ text: name, color })
        .then(({ data }) => {
          const updatedLabels = [...labels]
          updatedLabels.unshift(data)
          setLabels(updatedLabels)
          toggleModal()
        })
        .catch(() => setErrorModal(lang('dashboard_error-saving-label')))
        .finally(() => setLoadingModal(false))
    }
  }

  return (
    <div className='translations-labels-section'>
      <div className='translations-labels-section__header'>
        <div className='translations-labels-section__header-info'>
          <div className='translations-labels-section__header-description'>
            <p className='translations-labels-section__title'>{lang('dashboard_labels')}</p>
            <p className='translations-labels-section__description'>{lang('dashboard_manage-labels-translations')}</p>
          </div>
          <Button
            primary
            className='translations-labels-section__add-btn'
            value={lang('dashboard_add')}
            onClick={toggleModal}
            disabled={loading}
          />
        </div>
        <InputField inputValue={text} onChangeInput={handleOnChange} />
      </div>
      <div className='translations-labels-section__body'>
        {labels.map(label => (
          <LabelItem
            key={label._id}
            color={label.color}
            id={label._id}
            name={label.name}
            toggleModal={() => toggleModal(label._id)}
          />
        ))}
        {(!isAllLabels || loading) ? (
          <Button
            clearPrimary
            className='translations-labels-section__load-more-btn'
            value={lang('dashboard_load-more')}
            onClick={() => setPage(prevState => prevState + 1)}
            isSubmitting={loading}
          />
        ) : null}
        {!loading && isAllLabels && !labels.length ? (
          <p className='translations-labels-section__info-text'>
            {lang('dashboard_no-labels')}
          </p>
        ) : null}
      </div>

      {/* Modal */}
      {isOpenModal ? (
        <Modal
          isOpen={isOpenModal}
          toggle={toggleModal}
          data={labels.find(({ _id }) => _id === labelIdForEdit)}
          onSave={handleSave}
          loading={loadingModal}
          error={errorModal}
        />
      ) : null}
    </div>
  )
}

export default LabelsSection
